/* Helpers */
import * as React from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/browser';

import { isValidEmail } from '../../helpers/validation';
import Button from '../UI/Button';
import Input from '../UI/Input/Input';
import withCountryCode, { BuildUrlWithCountry } from '../RoutesHoc';
import { termsAndCondition, privacy, api } from '../../helpers/routes';
import { fontFamilies } from '../component-book/helpers/fonts';

/* Interfaces */
interface NewsletterSubscriptionHandlerProps {
  buildUrlWithCountry?: BuildUrlWithCountry;
}

/* Components */
class NewsletterSubscriptionHandler extends React.PureComponent<
  NewsletterSubscriptionHandlerProps,
  { success: boolean; validationFailed: boolean }
> {
  private readonly inputRef = React.createRef<HTMLInputElement>();

  constructor(props) {
    super(props);
    this.state = { success: false, validationFailed: false };
  }

  componentDidMount(): void {
    this.inputRef.current &&
      this.inputRef.current.addEventListener('invalid', () => {
        this.setState({ validationFailed: true });
      });
  }

  handleChange = () => {
    this.setState({ validationFailed: false });
  };

  handleSubmit = e => {
    if (e) {
      e.preventDefault();
    }

    const emailField = this.inputRef.current;

    if (!emailField || !isValidEmail(emailField.value)) {
      this.setState({ validationFailed: true });
      return;
    }

    emailField &&
      axios
        .post(api.newsletterSignup, { email: emailField.value })
        .then(() => this.setState({ success: true }))
        .catch(error => Sentry.captureException(error));
  };

  render() {
    const { validationFailed, success } = this.state;
    const { buildUrlWithCountry } = this.props;
    return (
      <form onSubmit={this.handleSubmit}>
        <p className="header">Share your email and save!</p>
        <p className="under-header">Subscribe now to save up to 30% on your favorite luxury items</p>
        {!success && (
          <>
            <div className="input-and-button">
              <div className="input-container">
                <Input
                  ref={this.inputRef}
                  handleChange={this.handleChange}
                  hasError={validationFailed}
                  height="46px"
                  justifyContent="center"
                  name="email"
                  paddingBottom={'0'}
                  placeholder="Your favorite email address"
                  required
                />
              </div>
              <Button type="submit">I'm in!</Button>
            </div>
            <div className="error">{validationFailed && 'Invalid email address'}</div>
            <p className="info">
              Opt out at anytime by clicking unsubscribe at the bottom of any email. By signing up you agree with our{' '}
              <a href={buildUrlWithCountry(termsAndCondition)}>Terms and Conditions</a> and{' '}
              <a href={buildUrlWithCountry(privacy)}>Privacy Policy</a>.
            </p>
          </>
        )}
        {success && <p className="thank-you-msg">Thank you for subscribing! ❤</p>}
        <style jsx>{`
          form {
            text-align: center;
            margin: 0;
            font-family: ${fontFamilies.light};
            padding: 0 10%;
            display: flex;
            flex: 1;
            flex-direction: column;
            justify-content: center;
          }

          .input-and-button {
            width: 100%;
            display: flex;
            padding: 10px 20px 0 20px;
          }

          .input-and-button > :global(*) {
            padding: 0px;
            margin: 0 2.5px;
          }

          .input-and-button > :global(button) {
            width: 120px;
          }

          .input-container {
            flex: 1;
          }

          .error {
            color: #b94a48;
            text-align: left;
            margin: 0 23px 0px 23px;
            height: 18px;
          }

          .header {
            text-transform: uppercase;
            font-family: ${fontFamilies.bold};
            font-size: 15px;
            letter-spacing: 1px;
          }

          .under-header {
            font-size: 15px;
          }

          .under-header {
            margin-bottom: ${success ? '45px' : '0'};
          }

          .info {
            margin: 0 30px;
            font-size: 11px;
          }
          .info > a {
            font-size: 11px;
            text-decoration: underline;
          }

          .thank-you-msg {
            height: 54px;
          }

          @media (max-width: 1023px) {
            form {
              padding: 40px 20px 0 20px;
            }

            .input-and-button {
              padding: 10px 0 0 0;
              flex-direction: column;
            }
            .input-and-button > :global(*) {
              margin: 2.5px 0;
            }
            .input-and-button > :global(button) {
              margin-top: 10px;
              width: 100%;
            }

            .error {
              margin: 0;
            }

            .info {
              margin: 0;
            }
          }
        `}</style>
      </form>
    );
  }
}

export const NewsletterSubscription = withCountryCode(NewsletterSubscriptionHandler);
